import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button, Card, Spinner } from "@/components/ui-kit";
import MathText from "@/components/MathText";
import Scratchpad from "@/components/Scratchpad";
import { Send, Sparkles, PencilRuler, X } from "lucide-react";

const MATH_KEYS = [
  ["½", "\\frac{}{}"], ["xⁿ", "^"], ["√", "\\sqrt{}"], ["×", "\\times "],
  ["÷", "\\div "], ["π", "\\pi "], ["≤", "\\le "], ["≥", "\\ge "], ["=", "="],
];

export default function Tutor() {
  const { studentId, student } = useOutletContext();
  const { skillId } = useParams();
  const [skills, setSkills] = useState([]);
  const [active, setActive] = useState(skillId || "");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pad, setPad] = useState(false);
  const scrollRef = useRef();
  const startRef = useRef(Date.now());

  useEffect(() => {
    api.get(`/students/${studentId}/plan`).then(({ data }) => {
      const list = (data.entries || []).map((e) => ({ id: e.skill_id, name: e.name }));
      setSkills(list);
      if (!active && list.length) setActive(list[0].id);
    });
  }, [studentId]);

  useEffect(() => {
    setMessages([{ role: "assistant", content: "Hi! I'm your math thinking partner. Tell me the problem you're working on, and I'll help you figure it out — one step at a time. 😊" }]);
    setSessionId(null);
  }, [active]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    if (!input.trim() || !active) return;
    const text = input.trim();
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post(`/students/${studentId}/tutor`, {
        skill_id: active, message: text, session_id: sessionId,
        duration_sec: Math.round((Date.now() - startRef.current) / 1000),
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I had trouble responding. Try again in a moment." }]);
    } finally { setBusy(false); startRef.current = Date.now(); }
  };

  const insertKey = (tex) => setInput((v) => v + (v && !v.endsWith(" ") ? " $" : "$") + tex + "$ ");

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 200px)" }}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={20} className="text-[var(--ns-gold)]" />
        <select data-testid="tutor-skill-select" value={active} onChange={(e) => setActive(e.target.value)}
          className="flex-1 rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-3 py-2 text-sm font-semibold">
          {skills.length === 0 && <option value="">Take the diagnostic first</option>}
          {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button data-testid="toggle-scratchpad" onClick={() => setPad(!pad)} className="p-2 rounded-lg border border-[var(--ns-border)] text-[var(--ns-muted)]" title="Scratchpad">
          {pad ? <X size={18} /> : <PencilRuler size={18} />}
        </button>
      </div>

      {pad && <div className="mb-3"><Scratchpad /></div>}

      <div ref={scrollRef} className="flex-1 overflow-y-auto ns-scroll space-y-3 pb-2" data-testid="tutor-messages">
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--ns-primary)] text-white rounded-br-sm" : "bg-[var(--ns-surface)] border border-[var(--ns-border)] rounded-bl-sm"}`}
              data-testid={`msg-${m.role}`}>
              <MathText text={m.content} />
            </div>
          </motion.div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-[var(--ns-surface)] border border-[var(--ns-border)] rounded-2xl px-4 py-3"><span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span></div></div>}
      </div>

      <div className="mt-2">
        <div className="flex gap-1.5 overflow-x-auto ns-scroll pb-2">
          {MATH_KEYS.map(([label, tex]) => (
            <button key={label} data-testid={`mathkey-${label}`} onClick={() => insertKey(tex)}
              className="shrink-0 rounded-lg border border-[var(--ns-border)] bg-[var(--ns-surface)] px-3 py-1.5 text-sm font-semibold">{label}</button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <textarea data-testid="tutor-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Show your thinking or ask a question…"
            className="flex-1 resize-none rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--ns-primary)]" />
          <Button data-testid="tutor-send" disabled={busy || !input.trim() || !active} onClick={send} className="px-4"><Send size={18} /></Button>
        </div>
      </div>
    </div>
  );
}

const Dot = ({ d = 0 }) => <span className="w-2 h-2 rounded-full bg-[var(--ns-subtle)] inline-block animate-bounce" style={{ animationDelay: `${d}s` }} />;
